import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';

import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import SelectMenu from '../ui/SelectMenu';
import PageHeader from '../ui/PageHeader';
import { useToast } from '../ui';
import { settingsAPI } from '../../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

/**
 * Reusable CRUD list page for Settings → Project Lists.
 *
 * Props:
 * - listType: string (e.g., 'lead_sources', 'competitors', 'customer_types')
 * - title: string (page heading)
 * - description: string (subtitle)
 * - fields: array of { key, label, type, required, placeholder, options }
 * - nameField: string (which field is the primary display name, default 'name')
 * - allowReorder: boolean (show drag handles)
 * - emptyMessage: string
 * - bare: boolean — when true, hides the internal PageHeader + top-right `+ Add`
 *   button so the caller can wrap this list inside a <SectionCard> (or similar
 *   surface) and drive the add flow itself. Callers should attach a ref and
 *   call `ref.current.openAdd()` from their own action slot. The in-empty-state
 *   "Add First Item" button remains, since it's contextual to the empty body.
 */
const CrudListPage = forwardRef(function CrudListPage({
  listType,
  title,
  description,
  fields = [{ key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Enter name' }],
  nameField = 'name',
  allowReorder = false,
  emptyMessage = 'No items yet. Add your first one to get started.',
  bare = false,
}, ref) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getList(listType);
      setItems(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [listType]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleAdd = useCallback(() => {
    setEditingItem(null);
    setModalOpen(true);
  }, []);

  // Expose imperative openAdd() so parents (e.g. SectionCard action slot) can
  // trigger the add flow when rendering in `bare` mode.
  useImperativeHandle(ref, () => ({ openAdd: handleAdd }), [handleAdd]);

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await settingsAPI.updateListItem(listType, editingItem.id, formData);
        toast.success('Item updated successfully');
      } else {
        await settingsAPI.addListItem(listType, formData);
        toast.success('Item added successfully');
      }
      setModalOpen(false);
      setEditingItem(null);
      await fetchItems();
    } catch (err) {
      throw new Error(err.message || 'Failed to save');
    }
  };

  const handleDelete = async (item) => {
    try {
      await settingsAPI.deleteListItem(listType, item.id);
      toast.success('Item deleted');
      setDeleteConfirm(null);
      await fetchItems();
    } catch (err) {
      setError('Failed to delete item');
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      </div>
    );
  }

  return (
    <div>
      {!bare && (
        <PageHeader
          title={title}
          subtitle={description}
          actions={
            <Button onClick={handleAdd}>
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add
            </Button>
          }
        />
      )}

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {bare ? (
        items.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">{emptyMessage}</p>
            <Button onClick={handleAdd} variant="secondary" size="sm" className="mt-4">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add First Item
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {items.map((item, index) => (
              <li
                key={item.id || index}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {allowReorder && (
                    <Bars3Icon className="h-4 w-4 text-gray-300 flex-shrink-0 cursor-grab" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item[nameField] || item.name}
                    </p>
                    {item.description && (
                      <p className="text-xs text-gray-500 truncate">{item.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    aria-label={`Edit ${item[nameField] || item.name}`}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(item)}
                    className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                    aria-label={`Delete ${item[nameField] || item.name}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : (
      <Card className="overflow-hidden">
        {items.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">{emptyMessage}</p>
            <Button onClick={handleAdd} variant="secondary" size="sm" className="mt-4">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add First Item
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {items.map((item, index) => (
              <li
                key={item.id || index}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {allowReorder && (
                    <Bars3Icon className="h-4 w-4 text-gray-300 flex-shrink-0 cursor-grab" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item[nameField] || item.name}
                    </p>
                    {item.description && (
                      <p className="text-xs text-gray-500 truncate">{item.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    aria-label={`Edit ${item[nameField] || item.name}`}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(item)}
                    className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                    aria-label={`Delete ${item[nameField] || item.name}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      )}

      {/* Add/Edit Modal */}
      <ItemFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingItem(null); }}
        item={editingItem}
        fields={fields}
        onSave={handleSave}
        title={editingItem ? `Edit ${(title || 'Item').replace(/s$/, '')}` : `Add ${(title || 'Item').replace(/s$/, '')}`}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogHeader onClose={() => setDeleteConfirm(null)}>Delete Item</DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to delete <strong>{deleteConfirm?.[nameField] || deleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
          </div>
        </DialogBody>
      </Dialog>
    </div>
  );
});

export default CrudListPage;

function ItemFormModal({ open, onClose, item, fields, onSave, title }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      const initial = {};
      fields.forEach(f => {
        initial[f.key] = item?.[f.key] || '';
      });
      setForm(initial);
      setError(null);
    }
  }, [open, item, fields]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    for (const field of fields) {
      if (field.required && !form[field.key]?.toString().trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }
    try {
      setSaving(true);
      setError(null);
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>{title}</DialogHeader>
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}

          {fields.map(field => (
            <div key={field.key}>
              <label htmlFor={`field-${field.key}`} className="block text-sm font-medium text-gray-700 mb-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.type === 'select' ? (
                <SelectMenu
                  id={`field-${field.key}`}
                  value={form[field.key] || ''}
                  onChange={(val) => setForm(prev => ({ ...prev, [field.key]: val }))}
                  placeholder="Select..."
                  options={field.options || []}
                  width="full"
                />
              ) : field.type === 'textarea' ? (
                <Textarea
                  id={`field-${field.key}`}
                  value={form[field.key] || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  rows={3}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              ) : (
                <Input
                  id={`field-${field.key}`}
                  type={field.type || 'text'}
                  value={form[field.key] || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )}
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
}
